import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Printer, UtensilsCrossed } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { staffAttApi, errMsg, fmtTime } from '@/services/staffAttendanceApi';

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const at12 = (hm) => new Date(`2000-01-01T${hm}:00`).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
const nice = (d) => {
  const x = new Date(`${d}T00:00:00`);
  return `${x.toLocaleDateString('en-IN', { weekday: 'short' })}, ${x.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} ${x.getFullYear()}`;
};

const openPdf = async (params) => {
  const win = window.open('', '_blank');
  const blob = await staffAttApi.foodAllowancePdf(params);
  const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  if (win) { win.location.href = url; return; }
  const a = document.createElement('a');
  a.href = url; a.download = `night_food_allowance_${params.date}.pdf`; document.body.appendChild(a); a.click(); a.remove();
};

const NotCounted = ({ rows }) => rows.length > 0 && (
  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="food-not-counted">
    <p className="font-semibold text-amber-900 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />{rows.length} checked out late but are NOT on the sheet</p>
    <ul className="mt-1 space-y-0.5 text-amber-900">
      {rows.map((r, i) => <li key={`${r.staff_id}-${i}`} data-testid={`food-not-counted-${i}`}><b>{r.staff_name}</b> ({r.section}, out {fmtTime(r.check_out)}) — {r.reason}</li>)}
    </ul>
  </div>
);

export const FoodAllowanceDialog = ({ date, section }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const params = { date, ...(section ? { section } : {}) };

  useEffect(() => {
    if (!open) return;
    setData(null);
    staffAttApi.foodAllowance(params).then(setData).catch((e) => toast.error(errMsg(e, 'Failed to load food allowance')));
  }, [open, date, section]); // eslint-disable-line react-hooks/exhaustive-deps

  const print = async () => {
    setBusy(true);
    try { await openPdf(params); } catch (e) { toast.error(errMsg(e, 'Could not make the PDF')); } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="border-emerald-500 text-emerald-700 bg-white" data-testid="food-allowance-btn">
        <UtensilsCrossed className="w-4 h-4 mr-1" />Food Allowance
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="food-allowance-dialog">
          <DialogHeader>
            <DialogTitle>Night Food Allowance — {nice(date)}</DialogTitle>
            <DialogDescription>Regular staff and drivers with an approved check-out after {data ? at12(data.after) : '…'} that night{section ? ` · ${section}` : ''}.</DialogDescription>
          </DialogHeader>
          {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600 my-8" /> : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2" data-testid="food-totals">
                <div className="rounded-lg bg-stone-100 p-3"><p className="text-xs text-stone-500">Staff</p><p className="text-2xl font-bold" data-testid="food-total-count">{data.totals.count}</p></div>
                <div className="rounded-lg bg-stone-100 p-3"><p className="text-xs text-stone-500">Per staff</p><p className="text-lg font-bold">{inr(data.food_rate)} + {inr(data.tea_rate)}</p><p className="text-[11px] text-stone-500">food + tea</p></div>
                <div className="rounded-lg bg-emerald-600 text-white p-3"><p className="text-xs opacity-80">Total</p><p className="text-2xl font-bold" data-testid="food-total-amount">{inr(data.totals.total)}</p></div>
              </div>
              {data.sections.map((s) => (
                <div key={s.section} className="rounded-lg border border-stone-200" data-testid={`food-section-${s.section}`}>
                  <div className="flex justify-between bg-stone-50 px-3 py-1.5 text-sm font-semibold"><span>{s.section}</span><span>{s.count} staff · {inr(s.total)}</span></div>
                  {data.rows.filter((r) => r.section === s.section).map((r) => (
                    <div key={`${r.staff_id}-${r.staff_name}`} className="flex justify-between px-3 py-1 text-sm border-t border-stone-100" data-testid={`food-row-${r.staff_id || r.staff_name}`}>
                      <span><b>{r.staff_name}</b> <span className="text-stone-500">{r.staff_id} · {r.type}</span></span>
                      <span className="font-mono text-stone-600">{fmtTime(r.check_in)} → {fmtTime(r.check_out)}</span>
                    </div>
                  ))}
                </div>
              ))}
              {data.rows.length === 0 && <p className="text-center text-stone-500 py-4" data-testid="food-empty">No regular staff or drivers checked out after {at12(data.after)} on this night.</p>}
              <NotCounted rows={data.not_counted} />
              <div className="flex justify-end">
                <Button onClick={print} disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="food-print-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}Print PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
